import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { when } from 'mobx';
import numeral from 'numeral';
import type { JSX } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IAggregatedStats } from '@feathr/blackbox';
import type { TTimeFrameValue } from '@feathr/components';
import { StatBox } from '@feathr/components';
import { StoresContext } from '@feathr/extender/state';

import * as styles from './AudienceEngagementStats.css';

interface IProps {
  timeFrameValue: TTimeFrameValue;
}

export function AudienceEngagementStats({ timeFrameValue }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { AggregatedStats } = useContext(StoresContext);
  const [aggregatedStats, setAggregatedStats] = useState<IAggregatedStats>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const filters = {
    metadata__date__gte: timeFrameValue[0],
    metadata__date__lte: timeFrameValue[1],
  };

  useEffect(() => {
    const fetchStats = async (): Promise<void> => {
      setIsLoading(true);
      const stats = AggregatedStats.aggregate({
        filters,
        model: 'account',
      });
      await when(() => !stats.isPending);
      setAggregatedStats(stats.toJS());
      setIsLoading(false);
    };
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFrameValue]);

  const audienceEngagementStats = {
    community: aggregatedStats?.num_persons_created ?? 0,
    connections:
      (aggregatedStats?.num_persons_with_ad_clicks ?? 0) +
      (aggregatedStats?.num_persons_with_ad_views ?? 0) +
      (aggregatedStats?.num_persons_with_page_link_clicks ?? 0) +
      (aggregatedStats?.num_persons_with_page_views ?? 0) +
      (aggregatedStats?.num_pinpoint_tracked_email_clicks ?? 0) +
      (aggregatedStats?.num_pinpoint_tracked_email_opens ?? 0),

    conversions: aggregatedStats?.num_conversions ?? 0,
  };

  return (
    <div className={styles.root}>
      <StatBox
        align={'center'}
        border={'right'}
        isLoading={isLoading}
        label={t('Community')}
        primary={numeral(audienceEngagementStats.community).format('0,0')}
        secondaryLabel={t('People')}
        statSize={'large'}
        theme={'purple'}
        tooltip={t(
          'This metric evaluates your success in acquiring new audience members and expanding your reach.',
        )}
        tooltipIcon={faInfoCircle}
      />
      <StatBox
        align={'center'}
        border={'none'}
        isLoading={isLoading}
        label={t('Connections')}
        primary={numeral(audienceEngagementStats.connections).format('0,0')}
        secondaryLabel={t('People')}
        statSize={'large'}
        theme={'cyan'}
        tooltip={t(
          'This metric measures your success in terms of deepening your engagement with existing audiences',
        )}
        tooltipIcon={faInfoCircle}
      />
      <StatBox
        align={'center'}
        border={'left'}
        isLoading={isLoading}
        label={t('Conversions')}
        primary={numeral(audienceEngagementStats.conversions).format('0,0')}
        secondaryLabel={t('People')}
        statSize={'large'}
        theme={'green'}
        tooltip={t(
          'This metric assesses your impact in terms of driving the actions and conversions that advance your organization’s mission.',
        )}
        tooltipIcon={faInfoCircle}
      />
    </div>
  );
}

export default AudienceEngagementStats;
